import {
  makeStyles,
  InputAdornment,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _pick from 'lodash/pick';
import mentorsAPI from '../../api/mentors';
import { TenantId } from '../../api/tenants/Tenant';
import { useMentorsContext } from '../../contexts/mentors-context';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { RouterContext } from '../../contexts/router-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { Link, Text, TextField } from '../common';
import List from '../common/list';
import MentorCard from '../list-cards/mentor-card';

const useStyles = makeStyles(() => ({
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  searchInput: {
    width: '100%',
  },
  searchInputWrapper: {
    width: '100%',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
}));

export default function MentorsList() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { rb } = useResourceBundles();
  const { user, hasAccessToAction } = useContext(UserContext);
  const { prevPath } = useContext(RouterContext);
  const { mentors, mentorsLogos, setMentors } = useMentorsContext();

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const { page, search } = useMemo(() => {
    const parsedQuery = _pick(searchParams, ['page', 'search']);
    return {
      page: parsedQuery.page ? Number(parsedQuery.page) : 0,
      search: (parsedQuery.search as string) || undefined,
    };
  }, [searchParams]);

  const debouncedTimer = useRef<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchValue, setSearchValue] = useState(search || '');

  const prevMentorId = useMemo(() => {
    if (prevPath?.includes?.('/mentors/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const mentorsIndex = splitPath.indexOf('mentors');
      const mentorId = splitPath[mentorsIndex + 1];

      if (mentorId && mentorId.length === 36) {
        return `card-${mentorId}`;
      }
    }
  }, [prevPath]);

  const prepareNewURL = useCallback(
    (nextSearchParams) => {
      return `${location.pathname}${
        Object.keys(nextSearchParams).length
          ? `?${stringifyQuery(nextSearchParams)}`
          : ''
      }`;
    },
    [location.pathname],
  );

  const handleSearchUpdate = useCallback(
    (search?: string) => {
      const clearSearchParams = {
        ...searchParams,
        page: 0,
        search,
      };
      history.replace(prepareNewURL(clearSearchParams));
    },
    [history, prepareNewURL, searchParams],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          handleSearchUpdate(e.target.value.trim());
        } else {
          handleSearchUpdate();
        }
      }, 800) as unknown as number;
    },
    [handleSearchUpdate],
  );

  const handleNextPage = useCallback(() => {
    if (!hasMorePages || isPageLoading) return;

    const nextSearchParams = {
      ...searchParams,
      page: page + 1,
    };
    history.replace(prepareNewURL(nextSearchParams));
  }, [hasMorePages, history, isPageLoading, page, prepareNewURL, searchParams]);

  const fetchMentors = useCallback(
    async ({
      userId,
      search,
      page = 0,
    }: {
      userId: TenantId;
      search?: string;
      page?: number;
    }) => {
      try {
        if (search) {
          return await mentorsAPI.search(search);
        } else {
          return await mentorsAPI.getMentorsAdvancedFilters(
            {
              tenantId: userId,
              status: 'Active',
            },
            page,
          );
        }
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.message === '404') {
          setHasMorePages(false);
          return [];
        }

        console.error(e);
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setLoadError(errorMessage);
        return [];
      }
    },
    [],
  );

  useEffect(() => {
    const loadMentors = async () => {
      if (!user) {
        return;
      }

      setIsLoading(true);
      setMentors([]);

      if (!search && searchValue.length > 2) {
        setSearchValue('');
      }

      const pages = Array.from({ length: page + 1 }, (_, i) => i);
      const promises = pages.map((p) =>
        fetchMentors({
          userId: user.id,
          search,
          page: p,
        }),
      );

      const loadedVenturesResult = await Promise.all(promises);
      const loadedVentures = loadedVenturesResult.flat();

      setMentors(loadedVentures);
      setIsLoading(false);
    };

    loadMentors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMentors, search, user]);

  useEffect(() => {
    const loadMentorsPage = async (page: number) => {
      if (!user || isLoading) {
        return;
      }

      setIsPageLoading(true);

      const loadedMentors = await fetchMentors({
        page,
        userId: user.id,
        search,
      });

      setMentors((prev) => [...prev, ...loadedMentors]);
      setIsPageLoading(false);
    };

    loadMentorsPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <List
      prevId={prevMentorId}
      loadError={loadError}
      isLoading={isLoading}
      isPageLoading={isPageLoading}
      hasMorePages={hasMorePages}
      onNextPage={!search ? handleNextPage : undefined}
      filter={
        <TextField
          className={cn(classes.searchInput, CLASS_TRACKING.FILTER_ELEMENT)}
          value={searchValue}
          onChange={handleSearch}
          placeholder='Search'
          disabled={isLoading || isPageLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          wrapperClassName={classes.searchInputWrapper}
          small
        />
      }>
      {!mentors.length ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='mentor-table-no-items'>
            This panel contains a list of {rb('mentors')}
            {search ? ` with a keyword "${search}"` : ''}
            .<br />
            {hasAccessToAction('mentor.create')
              ? 'We were not able to find information you requested, but feel free to add something new!'
              : ''}
          </Text>
          {hasAccessToAction('mentor.create') && (
            <MaterialButton
              component={Link}
              to={Pages.NEW_MENTOR}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              {rb('mentor-u')}
            </MaterialButton>
          )}
        </div>
      ) : (
        <div className={classes.listContainer} key={search}>
          {mentors.map((mentor) => (
            <MentorCard
              key={mentor.id}
              mentor={mentor}
              logo={mentorsLogos?.[mentor.id]}
              cardId={`card-${mentor.id}`}
            />
          ))}
        </div>
      )}
    </List>
  );
}
